'use client';

import { useMemo } from 'react';
import { Typography } from '@/components/ui/typography';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { useTranslations } from 'next-intl';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { ContactForm } from '@/components/frontend/common/contact-form';
import { ContactAddressSection } from '@/components/frontend/contact/contact-address-section';

type ContactAddressItem = { id: string; country: string; address: string; phones: string[]; email: string };

export function ContactSection({ initialAddresses }: { initialAddresses?: ContactAddressItem[] } = {}) {
    const t = useTranslations('home.contact');
    const sectionRef = useScrollAnimation({ threshold: 0.1 });

    const content = useMemo(
        () => ({
            pill: t('pillLabel'),
            title: t('title'),
            subtitle: t('subtitle'),
        }),
        [t]
    );

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="md"
            className="relative bg-white dark:bg-gray-950"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
            </div>

            <div className="relative z-10">
                <div className="mb-10">
                    <SectionHeading
                        pill={content.pill}
                        title={content.title}
                        subtitle={content.subtitle}
                        align="center"
                        maxWidthClass="max-w-4xl"
                        isVisible
                    />
                </div>
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                    <div
                        className={`transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            }`}
                    >
                        <ContactAddressSection initialAddresses={initialAddresses} />
                    </div>
                    <div
                        className={`transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            }`}
                        style={{ transitionDelay: '200ms' }}
                    >

                        <div className="rounded-2xl p-6 md:p-8 lg:p-8 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_14px_40px_rgba(15,23,42,0.08)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.45)]">
                            <Typography variant="h4" className="mb-4 text-lg text-gray-900 dark:text-white">
                                {content.title}
                            </Typography>
                            <ContactForm source="contact" />
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

